// RtcHostPeer — the browser side of a peer-to-peer host (docs/p2p.md).
//
// In P2P host mode a player's own browser IS the server: it runs the SERVER
// halves on a LocalServer (local-server.ts) and accepts remote peers over
// WebRTC. This is the browser mirror of the server's acceptWebRtcOffer
// (packages/server/src/webrtc-connection.ts) — where that terminates a browser
// offer with node-datachannel, this terminates it with the DOM
// RTCPeerConnection, so ONE codebase concept (adopt the offerer's two channels,
// answer non-trickle, expose a reliable + unreliable send) has a Node form (the
// hosted server) and a browser form (a peer host).
//
// The JOINER is unchanged: it is a plain client whose bus socket is the same
// WebRtcBusSocket we already ship — it offers, creates the two channels, and
// only the SIGNALING TRANSPORT differs (a broker relay instead of an HTTP POST
// the joiner can't make to another browser). So the joiner (offerer) creates:
//   "reliable"   → ordered, reliable. Handoff (message #1), reliable frames,
//                  and the host's TEXT control frames (peer_left / redirect).
//   "unreliable" → unordered, maxRetransmits:0. The unreliable egress class.
// and we ADOPT them here by label via pc.ondatachannel.
//
// Both channels ride ONE RTCPeerConnection. Signaling is non-trickle: the
// joiner gathers fully before sending its offer, and we gather our answer fully
// before returning it, so one offer→answer exchange completes the handshake
// with no separate candidate channel.

// WebRTC default ICE servers for the host side — a public STUN server so a host
// behind NAT can still surface a server-reflexive candidate. (Browser↔browser
// often needs TURN for symmetric-NAT pairs; that is a later drop-in, see
// docs/p2p.md — for now such pairs simply fail to connect.)
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
];

// Cap on ICE gathering before we answer with whatever candidates we have. Host
// candidates gather in milliseconds; this only bounds a slow/unreachable STUN
// tail (a reachable host connects on host candidates alone).
const ICE_GATHER_TIMEOUT_MS = 3000;

import { encodeControlMessage } from "@multinostalgia/munos";
import type { HostedClient } from "./peer-host.js";

// One inbound message from a peer: binary game frames (as bytes) or a TEXT
// control frame (as a string). The coordinator (peer-host.ts) demuxes them.
export type HostPeerMessage = Uint8Array | string;

// Injectable seams for headless testing, matching the codebase's
// factory-injection style. Tests pass node-datachannel's W3C polyfill as
// createPeerConnection so the answerer runs over real ICE/DTLS/SCTP with no DOM.
export interface RtcHostPeerDeps {
  createPeerConnection?: (config: RTCConfiguration) => RTCPeerConnection;
  iceServers?: RTCIceServer[];
}

export interface AcceptedPeer {
  // The answer SDP to route back to the joiner (through the broker).
  answerSdp: string;
  // The live peer transport, ready for the coordinator to attach onMessage /
  // onClose. Its channels open once the DTLS/SCTP handshake completes.
  peer: RtcHostPeer;
}

// Send bytes over a DataChannel. Same cast the rest of the codebase uses for DOM
// byte APIs (webrtc-bus-socket.ts sendBinary): our frames are always regular,
// non-shared Uint8Arrays from the envelope codec, so the ArrayBuffer view cast
// is sound.
function sendBinary(dc: RTCDataChannel, data: Uint8Array): void {
  dc.send(data as unknown as ArrayBuffer);
}

export class RtcHostPeer implements HostedClient {
  private readonly pc: RTCPeerConnection;
  private reliable: RTCDataChannel | null = null;
  private unreliable: RTCDataChannel | null = null;

  private messageCb: ((data: HostPeerMessage) => void) | null = null;
  private closeCb: (() => void) | null = null;
  private closed = false;

  // Inbound messages that arrived before the coordinator attached onMessage.
  // A channel's onmessage can fire around the time it opens, before the
  // coordinator (which reacts to the accept() resolving) wires its listener —
  // critically the connect handoff, message #1. Park them and replay in order
  // so the handoff is never lost and never usurped by a later frame. Mirrors
  // RtcClientSocket.earlyMessages on the server.
  private readonly earlyMessages: HostPeerMessage[] = [];

  // Cross-channel ordering guard: the server treats the FIRST message as the
  // connect handoff, sent on the reliable channel. An unreliable datagram that
  // races ahead of it (independent SCTP streams can reorder across channels) is
  // held until the first reliable message is delivered, so the handoff stays
  // message #1. Mirrors RtcClientSocket.pendingUnreliable.
  private firstReliableSeen = false;
  private readonly pendingUnreliable: Uint8Array[] = [];

  private constructor(pc: RTCPeerConnection) {
    this.pc = pc;
    // Adopt the channels the joiner (offerer) created, keyed by label.
    this.pc.ondatachannel = (ev: RTCDataChannelEvent) => this.adoptChannel(ev.channel);
    // A peer connection that fails / closes / drops is a disconnect — surface
    // it so the coordinator frees the slot exactly as a WebSocket close would.
    this.pc.onconnectionstatechange = () => {
      const st = this.pc.connectionState;
      if (st === "failed" || st === "closed" || st === "disconnected") this.handleClose();
    };
  }

  // Accept a joiner's offer: build the peer, adopt its channels, answer
  // non-trickle. Resolves with the answer SDP + the live peer once ICE
  // gathering completes (the channels open shortly after, when DTLS/SCTP does).
  static async accept(offerSdp: string, deps: RtcHostPeerDeps = {}): Promise<AcceptedPeer> {
    const makePc = deps.createPeerConnection ?? ((c) => new RTCPeerConnection(c));
    const pc = makePc({ iceServers: deps.iceServers ?? DEFAULT_ICE_SERVERS });
    const peer = new RtcHostPeer(pc);

    await pc.setRemoteDescription({ type: "offer", sdp: offerSdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await peer.iceGatheringComplete();
    const answerSdp = pc.localDescription?.sdp ?? answer.sdp ?? "";
    return { answerSdp, peer };
  }

  private adoptChannel(dc: RTCDataChannel): void {
    dc.binaryType = "arraybuffer";
    if (dc.label === "reliable") {
      this.reliable = dc;
      dc.onmessage = (ev) => this.onReliableMessage(ev.data);
      dc.onclose = () => this.handleClose();
      dc.onerror = () => { /* surfaced via connectionstatechange → close */ };
    } else if (dc.label === "unreliable") {
      this.unreliable = dc;
      dc.onmessage = (ev) => this.onUnreliableMessage(ev.data);
      // The unreliable channel closing alone isn't a session close — the
      // reliable channel (and pc state) governs liveness.
    }
    // Unknown labels are ignored: a future protocol may add channels; the
    // reliable channel still carries the session.
  }

  private onReliableMessage(data: unknown): void {
    this.deliverMessage(normalize(data));
    if (!this.firstReliableSeen) {
      this.firstReliableSeen = true;
      for (const held of this.pendingUnreliable) this.deliverMessage(held);
      this.pendingUnreliable.length = 0;
    }
  }

  private onUnreliableMessage(data: unknown): void {
    const msg = normalize(data);
    // Hold unreliable traffic until the handoff has been delivered.
    if (!this.firstReliableSeen && msg instanceof Uint8Array) {
      this.pendingUnreliable.push(msg);
      return;
    }
    this.deliverMessage(msg);
  }

  private deliverMessage(data: HostPeerMessage): void {
    if (this.messageCb) this.messageCb(data);
    else this.earlyMessages.push(data);
  }

  // Attach the inbound listener; drains anything parked before it (the handoff
  // and any frames in the open/attach gap), in order. Called once by the
  // coordinator right after accept() resolves.
  onMessage(cb: (data: HostPeerMessage) => void): void {
    this.messageCb = cb;
    if (this.earlyMessages.length > 0) {
      const queued = this.earlyMessages.splice(0);
      for (const m of queued) cb(m);
    }
  }

  onClose(cb: () => void): void {
    this.closeCb = cb;
    if (this.closed) cb();
  }

  // Reliable/ordered send: binary game frames and TEXT control frames.
  send(data: Uint8Array | string): void {
    const dc = this.reliable;
    if (!dc || dc.readyState !== "open") return;
    if (typeof data === "string") dc.send(data);
    else sendBinary(dc, data);
  }

  // Unreliable/unordered fast-path. Falls back to the reliable channel before
  // the unreliable one is up (a narrow bring-up window), so nothing is lost.
  sendUnreliable(data: Uint8Array): void {
    const un = this.unreliable;
    if (un && un.readyState === "open") { sendBinary(un, data); return; }
    const rel = this.reliable;
    if (rel && rel.readyState === "open") sendBinary(rel, data);
  }

  // ----- HostedClient: the LocalServer's per-slot sink delivers here -----

  // A coalesced server→client datagram for this peer. `reliable` picks the
  // channel: the reliable class rides the ordered channel, the unreliable class
  // WebRTC's unordered, zero-retransmit one.
  deliver(bytes: Uint8Array, reliable: boolean): void {
    if (reliable) this.send(bytes);
    else this.sendUnreliable(bytes);
  }

  // An interest-management leave, sent as a TEXT control frame on the reliable
  // channel → the joiner's `event peer_left(slot)`. Same shape the loopback and
  // the hosted server emit.
  deliverPeerLeft(subject: number): void {
    this.send(encodeControlMessage({ type: "peer_left", slot: subject }));
  }

  close(): void {
    this.handleClose();
  }

  private handleClose(): void {
    if (this.closed) return;
    this.closed = true;
    try { this.reliable?.close(); } catch { /* already gone */ }
    try { this.unreliable?.close(); } catch { /* already gone */ }
    try { this.pc.close(); } catch { /* already gone */ }
    if (this.closeCb) this.closeCb();
  }

  private iceGatheringComplete(): Promise<void> {
    if (this.pc.iceGatheringState === "complete") return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        this.pc.removeEventListener("icegatheringstatechange", onChange);
        clearTimeout(timer);
        resolve();
      };
      const onChange = (): void => {
        if (this.pc.iceGatheringState === "complete") finish();
      };
      this.pc.addEventListener("icegatheringstatechange", onChange);
      const timer = setTimeout(finish, ICE_GATHER_TIMEOUT_MS);
    });
  }
}

// A DOM RTCDataChannel delivers binary as ArrayBuffer (binaryType set above) and
// text as string. Normalize binary to a standalone Uint8Array; keep strings for
// the control plane. Matches normalize() in the server's webrtc-connection.ts.
function normalize(data: unknown): HostPeerMessage {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  return new Uint8Array(0);
}
